import './index.css';
// Must evaluate before '@/scenes/router' creates the router, see initDatadogRum.
import './initDatadogRum';

import { ErrorBoundary } from '@datadog/browser-rum-react';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { createRoot } from 'react-dom/client';

import { Auth0ProviderWithNavigate } from '@/components/auth/Auth0ProviderWithNavigate';
import ErrorFallback from '@/components/ErrorBoundaryFallback';
import Providers from '@/components/Providers';
import RouterProvider from '@/scenes/router';

window.addEventListener('vite:preloadError', async (event) => {
  event.preventDefault();
  // Get current count from session storage or initialize to 0
  const reloadCount = parseInt(
    sessionStorage.getItem('vitePreloadErrorCount') || '0',
    10
  );

  // Check if we've already tried 3 times
  if (reloadCount >= 2) {
    console.warn(
      'Vite preload has failed multiple times. Stopping automatic reload.'
    );
    // Optionally show a user-facing message here
    return;
  }

  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch (cleanupError) {
    console.error(cleanupError);
  }
  //
  // Increment and save the counter
  sessionStorage.setItem('vitePreloadErrorCount', (reloadCount + 1).toString());

  console.log(`Reloading page (attempt ${reloadCount + 1} of 2)...`);
  window.location.reload(); // for example, refresh the page
});

// If the browser doesn't support Intl.Locale or it doesn't have the necessary features, load the polyfill.
void (async function loadIntlLocalePolyfill() {
  if (
    (
      await import(
        /* webpackChunkName: 'Intl-should-polyfill' */ '@formatjs/intl-locale/should-polyfill'
      )
    ).shouldPolyfill()
  ) {
    await import(
      /* webpackChunkName: 'Intl-locale-polyfill' */ '@formatjs/intl-locale/polyfill'
    );
  }
})();

// If the browser doesn't support Intl.supportedValuesOf, load the polyfill.
void (async function loadIntlEnumeratorPolyfill() {
  if (
    (
      await import(
        /* webpackChunkName: 'Intl-enumerator-should-polyfill' */ '@formatjs/intl-enumerator/should-polyfill.js'
      )
    ).shouldPolyfill()
  ) {
    await import(
      /* webpackChunkName: 'Intl-enumerator-polyfill' */ '@formatjs/intl-enumerator/polyfill.js'
    );
  }
})();

void (async () => {
  const root = createRoot(document.getElementById('root') as HTMLElement);
  const clientSideID = import.meta.env.VITE_LD_CLIENT_SIDE_ID;
  const LDProvider = await asyncWithLDProvider({
    clientSideID: clientSideID,
  });

  root.render(
    <React.StrictMode>
      <ErrorBoundary fallback={ErrorFallback}>
        <Auth0ProviderWithNavigate>
          <LDProvider>
            <Providers>
              <RouterProvider />
            </Providers>
          </LDProvider>
        </Auth0ProviderWithNavigate>
      </ErrorBoundary>
    </React.StrictMode>
  );
})();
